'use client';

import 'src/global.css';
import { Inter, Nunito_Sans } from 'next/font/google';

import i18next from 'i18next';
import { APIProvider } from '@vis.gl/react-google-maps';

import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter';

import { CONFIG } from 'src/global-config';
import { LocalizationProvider } from 'src/locales';
import { themeConfig, ThemeProvider } from 'src/theme';
import { I18nProvider } from 'src/locales/i18n-provider';
import { themeOverrides } from 'src/theme/theme-overrides';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import ReactQueryProvider from 'src/components/providers/react-query-provider';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';
import { ZohoSalesIQ } from 'src/utils/zoho-sales-IQ';
import { useEffect } from 'react';
import Clarity from '@microsoft/clarity';
import Script from 'next/script';
// layout.jsx or _app.jsx

// ----------------------------------------------------------------------

const inter = Inter({ subsets: ['latin'], display: 'swap', variable: '--font-inter' });
const nunito = Nunito_Sans({ subsets: ['latin'], display: 'swap', variable: '--font-nunito' });

export default function ClientLayout({ children }) {
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENV === 'qa') {
      Clarity.init(process.env.NEXT_PUBLIC_CLARITY_QA_PROJECT_ID);
    }
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENV === 'prod') {
      Clarity.init(process.env.NEXT_PUBLIC_CLARITY_PROD_PROJECT_ID);
    }
  }, []);
  const getLanguage = () => i18next.language || 'ar';

  // ✅ Define a Content Security Policy
  // const csp = `
  //   default-src 'self';
  //   script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.google-analytics.com https://www.googletagmanager.com https://maps.googleapis.com https://www.gstatic.com https://salesiq.zohopublic.com;
  //   style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
  //   img-src 'self' data: https:;
  //   connect-src 'self' https://www.google-analytics.com https://www.googletagmanager.com https://maps.googleapis.com https://api.zoho.com https://accounts.zoho.com https://api.iconify.design https://api.simplesvg.com https://api.unisvg.com https://salesiq.zohopublic.com/ https://api.zoho.com https://accounts.zoho.com https://salesiq.zohopublic.com https://corporate-services-qa.shahen-sa.com/;
  //   font-src 'self' https://fonts.gstatic.com;
  //   object-src 'none';
  //   base-uri 'self';
  //   form-action 'self';
  //   upgrade-insecure-requests;
  // `
  //   .replace(/\s{2,}/g, ' ')
  //   .trim();
  //   const csp = `
  //   default-src 'self';
  //   script-src 'self' 'unsafe-inline' 'unsafe-eval'
  //     https://www.google-analytics.com
  //     https://www.googletagmanager.com
  //     https://maps.googleapis.com
  //     https://www.gstatic.com
  //     https://salesiq.zohopublic.com
  //     https://js.zohocdn.com
  //     https://js.zohostatic.com;
  //   style-src 'self' 'unsafe-inline'
  //     https://fonts.googleapis.com
  //     https://static.zohocdn.com
  //     https://js.zohocdn.com
  //     https://js.zohostatic.com;
  //   img-src 'self' data: https:;
  //   connect-src 'self'
  //     https://www.google-analytics.com
  //     https://www.googletagmanager.com
  //     https://maps.googleapis.com
  //     https://api.zoho.com
  //     https://accounts.zoho.com
  //     https://vts.zohopublic.com
  //     wss://vts.zohopublic.com
  //     https://salesiq.zohopublic.com
  //     https://api.iconify.design
  //     https://api.simplesvg.com
  //     https://api.unisvg.com
  //     https://corporate-services-qa.shahen-sa.com;
  //   font-src 'self'
  //     https://fonts.gstatic.com
  //     https://static.zohocdn.com
  //     https://js.zohocdn.com
  //     https://js.zohostatic.com;
  //   object-src 'none';
  //   base-uri 'self';
  //   form-action 'self';
  //   upgrade-insecure-requests;
  // `;

  return (
    <html suppressHydrationWarning className={`${inter.variable} ${nunito.variable}`}>
      <head>
        {/* ✅ Global Content Security Policy */}
        {/* <meta httpEquiv="Content-Security-Policy" content={csp} /> */}

        {/* ✅ Favicon and basic icons */}
        <link rel="icon" href={`${CONFIG.assetsDir}/favicon.png`} />
        <link rel="apple-touch-icon" href={`${CONFIG.assetsDir}/favicon.png`} />

        <title>Shahen Logistics</title>
        <meta
          name="description"
          content="Book trucks online across Saudi Arabia with Shahen — Smart logistics solutions powered by Feker Communications & IT."
        />
        <Script async src="https://www.googletagmanager.com/gtag/js?id=AW-10878462618"></Script>
        <Script id="google-ads" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-10878462618');
        `}
        </Script>
      </head>
      <body>
        <InitColorSchemeScript
          attribute={themeConfig.cssVariables.colorSchemeSelector}
          modeStorageKey={themeConfig.modeStorageKey}
          defaultMode={themeConfig.defaultMode}
        />

        <APIProvider
          apiKey={CONFIG.apiKey}
          libraries={['places']}
          region="SA"
          language={getLanguage()}
        >
          <ReactQueryProvider>
            <I18nProvider>
              <SettingsProvider defaultSettings={defaultSettings}>
                <LocalizationProvider>
                  <AppRouterCacheProvider options={{ key: 'css' }}>
                    <ThemeProvider
                      themeOverrides={themeOverrides}
                      modeStorageKey={themeConfig.modeStorageKey}
                      defaultMode={themeConfig.defaultMode}
                    >
                      <MotionLazy>
                        <Snackbar />
                        <ZohoSalesIQ />
                        <ProgressBar />
                        <SettingsDrawer defaultSettings={defaultSettings} />
                        {children}
                      </MotionLazy>
                    </ThemeProvider>
                  </AppRouterCacheProvider>
                </LocalizationProvider>
              </SettingsProvider>
            </I18nProvider>
          </ReactQueryProvider>
        </APIProvider>
      </body>
    </html>
  );
}
